// The React Flow adapter. Everything React Flow specific lives in this folder:
// mapping items to nodes, its controlled-mode bookkeeping (measured sizes,
// selection), and turning its gestures into the shell's intents.

import { groupMembers, type Item } from '@shared/board'
import { lockToAxis, type Rect, union, type Vec } from '@shared/whiteboard/geometry'
import { gridSpacing } from '@shared/whiteboard/grid'
import { computeSnap, type SnapGuides } from '@shared/whiteboard/snapping'
import {
  Background,
  BackgroundVariant,
  ConnectionMode,
  type Edge,
  type EdgeChange,
  MarkerType,
  type Node,
  type NodeChange,
  ReactFlow,
  SelectionMode,
  useReactFlow,
  useStore,
  type Viewport,
  ViewportPortal,
} from '@xyflow/react'
import { clsx } from 'clsx'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { isComposing } from '@/lib/ime'
import { readPref, writePref } from '@/lib/storage'
import { type EngineApi, type EngineProps, type ScreenRect } from '../types'
import { FloatingEdge, LinkEdge } from './FloatingEdge'
import { NodeCallbacksContext, nodeTypes } from './nodes'
import { ZoomControls } from './ZoomControls'

const edgeTypes = { floating: FloatingEdge, link: LinkEdge }

type Size = { width: number; height: number }

function isEditable(target: EventTarget | null) {
  const el = target as HTMLElement | null
  return !!el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))
}

/** Items whose size is set by the model rather than by their content. */
function hasFixedSize(item: Item): boolean {
  return (
    item.kind === 'group' ||
    item.kind === 'image' ||
    (item.kind === 'card' && item.data.fixedHeight != null)
  )
}

/**
 * In controlled mode React Flow learns sizes through `dimensions` changes and
 * expects them back on the node as `measured`. A node object without it counts
 * as unmeasured and is hidden until re-measured, which drops focus and the
 * caret of any editor inside it. So every node carries its last measurement.
 */
function toNode(
  item: Item,
  pos: Vec | undefined,
  box: Partial<Rect> | undefined,
  selected: boolean,
  measured: Size | undefined,
  resizingHeight: boolean,
  hidden: boolean,
): Node {
  let view = item
  if (box?.w !== undefined || box?.h !== undefined) {
    view = { ...item, w: box.w ?? item.w, h: box.h ?? item.h }
    if (view.kind === 'card' && resizingHeight)
      view = { ...view, data: { ...view.data, fixedHeight: box.h ?? null } }
  }
  const fixed = hasFixedSize(view) || box?.h !== undefined
  const height = view.kind === 'card' ? (view.data.fixedHeight ?? view.h) : view.h
  return {
    id: item.id,
    type: item.kind,
    position: pos ?? { x: box?.x ?? item.x, y: box?.y ?? item.y },
    data: { item: view },
    selected,
    hidden,
    dragHandle: '.moot-drag',
    zIndex: item.kind === 'group' ? item.z - 100_000 : item.z,
    ...(item.kind === 'group' && { style: { pointerEvents: 'none' as const } }),
    ...(fixed ? { width: view.w, height } : {}),
    ...(measured && { measured }),
  }
}

function BoardBackground() {
  const zoom = useStore((s) => s.transform[2])
  return (
    <Background
      variant={BackgroundVariant.Dots}
      gap={gridSpacing(zoom)}
      size={1.3}
      color="var(--dot)"
    />
  )
}

function Guides({ guides }: { guides: SnapGuides }) {
  const zoom = useStore((s) => s.transform[2])
  const t = 1 / zoom
  return (
    <ViewportPortal>
      {guides.vertical.map((x) => (
        <div
          key={`v${x}`}
          className="moot-guide pointer-events-none absolute"
          style={{ left: x - t / 2, top: -1e5, width: t, height: 2e5 }}
        />
      ))}
      {guides.horizontal.map((y) => (
        <div
          key={`h${y}`}
          className="moot-guide pointer-events-none absolute"
          style={{ top: y - t / 2, left: -1e5, height: t, width: 2e5 }}
        />
      ))}
    </ViewportPortal>
  )
}

type Drag = {
  primary: string
  start: Map<string, Vec>
  grabbed: string[]
  bounds: Rect
  statics: Rect[]
  cancelled: boolean
}

/** How far `avoid` covers the canvas from its left and right edges (screen px). */
function coveredSides(canvas: DOMRect, avoid: ScreenRect | undefined) {
  if (!avoid || avoid.bottom <= canvas.top || avoid.top >= canvas.bottom)
    return { left: 0, right: 0 }
  const from = Math.max(avoid.left, canvas.left)
  const to = Math.min(avoid.right, canvas.right)
  if (to <= from) return { left: 0, right: 0 }
  // A panel along one edge: whichever edge it is nearer.
  return from - canvas.left <= canvas.right - to
    ? { left: to - canvas.left, right: 0 }
    : { left: 0, right: canvas.right - from }
}

export function XyflowEngine(props: EngineProps) {
  const { state, selectedItems, selectedEdges } = props
  const flow = useReactFlow()
  const wrapper = useRef<HTMLDivElement>(null)
  const [dragPos, setDragPosState] = useState<Map<string, Vec>>(() => new Map())
  // Mirror of dragPos for event handlers: side effects never run inside updaters.
  const dragPosRef = useRef(dragPos)
  const setDragPos = useCallback((next: Map<string, Vec>) => {
    dragPosRef.current = next
    setDragPosState(next)
  }, [])
  const [guides, setGuides] = useState<SnapGuides>({ vertical: [], horizontal: [] })
  const [resizeBox, setResizeBox] = useState<Map<string, Partial<Rect>>>(() => new Map())
  // Height when a resize began, to tell a height change from a width-only one.
  const [resizeStartH, setResizeStartH] = useState<Map<string, number>>(() => new Map())
  const [measured, setMeasured] = useState<Map<string, Size>>(() => new Map())
  const [spaceHeld, setSpaceHeld] = useState(false)
  const drag = useRef<Drag | null>(null)
  const viewportListeners = useRef(new Set<() => void>())
  const resizing = useRef(new Set<string>())
  // Latest selection, updated synchronously: node and edge select changes can
  // arrive in the same tick, before the shell re-renders with new props.
  const sel = useRef({ items: selectedItems, edges: selectedEdges })
  useLayoutEffect(() => {
    sel.current = { items: selectedItems, edges: selectedEdges }
  }, [selectedItems, selectedEdges])

  // ---------- engine api ----------

  // Settled once the first view is in place (see EngineApi.settled).
  const [firstView] = useState(() => {
    let resolve = () => {}
    const promise = new Promise<void>((r) => {
      resolve = r
    })
    return { promise, resolve }
  })

  // Built once per mount rather than on every render, so the shell's engine
  // slot only changes when the engine does (focus.ts compares it).
  const api = useMemo<EngineApi>(
    () => ({
      settled: () => firstView.promise,
      screenToBoard: (p) => flow.screenToFlowPosition(p),
      viewportCenter: () => {
        const r = wrapper.current?.getBoundingClientRect()
        if (!r) return { x: 0, y: 0 }
        return flow.screenToFlowPosition({ x: r.left + r.width / 2, y: r.top + r.height / 2 })
      },
      zoom: () => flow.getZoom(),
      onViewportChange: (fn) => {
        viewportListeners.current.add(fn)
        return () => {
          viewportListeners.current.delete(fn)
        }
      },
      drawnSize: (id) => {
        const n = flow.getInternalNode(id)
        const w = n?.measured.width
        const h = n?.measured.height
        return w && h ? { w, h } : null
      },
      fitRect: (rect, opts) => {
        const r = wrapper.current?.getBoundingClientRect()
        if (!r) return
        const { left, right } = coveredSides(r, opts?.avoid)
        const width = Math.max(r.width - left - right, r.width * 0.3)
        // Fit with room around it, but never zoom in past the current level (or 100%).
        const fit = Math.min(
          (width * 0.7) / Math.max(rect.w, 1),
          (r.height * 0.7) / Math.max(rect.h, 1),
        )
        const zoom = Math.max(0.15, Math.min(fit, Math.max(flow.getZoom(), 1)))
        // Centre it in the uncovered part: shift the camera by half the difference.
        const shift = (left - right) / 2 / zoom
        void flow.setCenter(rect.x + rect.w / 2 - shift, rect.y + rect.h / 2, {
          zoom,
          duration: 450,
        })
      },
      flash: (ids) => {
        const root = wrapper.current
        if (!root) return
        for (const id of ids) {
          const el = root.querySelector<HTMLElement>(
            `.react-flow__node[data-id="${CSS.escape(id)}"]`,
          )
          if (!el) continue
          el.classList.remove('moot-flash')
          // Restart the animation if it is already running (reading layout forces a reflow).
          el.getBoundingClientRect()
          el.classList.add('moot-flash')
          setTimeout(() => {
            el.classList.remove('moot-flash')
          }, 1800)
        }
      },
    }),
    [flow, firstView],
  )
  const { onReady } = props
  useEffect(() => {
    onReady(api)
  }, [api, onReady])

  // ---------- mapping ----------

  const nodes = useMemo(
    () =>
      [...state.items.values()].map((i) => {
        const box = resizeBox.get(i.id)
        const startH = resizeStartH.get(i.id)
        const resizingHeight =
          box?.h !== undefined && startH !== undefined && Math.abs(box.h - startH) > 1
        return toNode(
          i,
          dragPos.get(i.id),
          box,
          selectedItems.has(i.id),
          measured.get(i.id),
          resizingHeight,
          props.hidden.has(i.id),
        )
      }),
    [state.items, dragPos, resizeBox, resizeStartH, selectedItems, measured, props.hidden],
  )
  const edges = useMemo<Edge[]>(
    () => [
      ...[...state.edges.values()].map((e): Edge => ({
        id: e.id,
        source: e.from,
        target: e.to,
        type: 'floating',
        data: { edge: e },
        selected: selectedEdges.has(e.id),
        hidden: props.hidden.has(e.from) || props.hidden.has(e.to),
        markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: 'var(--edge)' },
      })),
      ...props.links.map((l): Edge => ({
        id: `link:${l.id}`,
        source: l.from,
        target: l.to,
        type: 'link',
        data: { kind: l.kind },
        selectable: false,
        deletable: false,
        focusable: false,
        zIndex: -1,
      })),
    ],
    [state.edges, selectedEdges, props.hidden, props.links],
  )

  // ---------- changes ----------

  const { onSelectionChange, onMeasure } = props
  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      let items: Set<string> | null = null
      for (const ch of changes) {
        if (ch.type === 'select') {
          items ??= new Set(sel.current.items)
          if (ch.selected) items.add(ch.id)
          else items.delete(ch.id)
        } else if (ch.type === 'dimensions' && ch.dimensions) {
          const { width, height } = ch.dimensions
          setMeasured((m) => {
            const cur = m.get(ch.id)
            return cur?.width === width && cur.height === height
              ? m
              : new Map(m).set(ch.id, { width, height })
          })
          if (ch.resizing) {
            resizing.current.add(ch.id)
            setResizeStartH((m) => {
              if (m.has(ch.id)) return m
              const it = state.items.get(ch.id)
              const h0 = it?.kind === 'card' ? (it.data.fixedHeight ?? it.h) : (it?.h ?? height)
              return new Map(m).set(ch.id, h0)
            })
            setResizeBox((m) => new Map(m).set(ch.id, { ...m.get(ch.id), w: width, h: height }))
          } else if (ch.resizing === false) {
            resizing.current.delete(ch.id)
            setResizeStartH((m) => {
              const n = new Map(m)
              n.delete(ch.id)
              return n
            })
            setResizeBox((m) => {
              const n = new Map(m)
              n.delete(ch.id)
              return n
            })
          } else {
            const item = state.items.get(ch.id)
            if (item && !hasFixedSize(item)) onMeasure(ch.id, Math.round(width), Math.round(height))
          }
        } else if (
          ch.type === 'position' &&
          ch.position &&
          ch.dragging !== true &&
          resizing.current.has(ch.id)
        ) {
          // Resizing from a top/left edge also moves the node.
          const pos = ch.position
          setResizeBox((m) => new Map(m).set(ch.id, { ...m.get(ch.id), x: pos.x, y: pos.y }))
        }
      }
      if (items) {
        sel.current = { ...sel.current, items }
        onSelectionChange(items, new Set(sel.current.edges))
      }
    },
    [state.items, onSelectionChange, onMeasure],
  )

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      let edges: Set<string> | null = null
      for (const ch of changes) {
        if (ch.type !== 'select') continue
        edges ??= new Set(sel.current.edges)
        if (ch.selected) edges.add(ch.id)
        else edges.delete(ch.id)
      }
      if (edges) {
        sel.current = { ...sel.current, edges }
        onSelectionChange(new Set(sel.current.items), edges)
      }
    },
    [onSelectionChange],
  )

  // ---------- dragging: group members follow, snapping, ⇧ axis lock, Esc cancels ----------

  const onNodeDragStart = useCallback(
    (_: unknown, node: Node, dragged: Node[]) => {
      const start = new Map<string, Vec>()
      for (const n of dragged) start.set(n.id, { ...n.position })
      for (const n of dragged) {
        if (state.items.get(n.id)?.kind !== 'group') continue
        for (const m of groupMembers(state, n.id))
          if (!start.has(m.id)) start.set(m.id, { x: m.x, y: m.y })
      }
      const moving = [...start.keys()]
        .map((id) => state.items.get(id))
        .filter((i): i is Item => !!i)
      const bounds = union(moving.map((i) => ({ ...i, ...start.get(i.id) }))) ?? {
        x: 0,
        y: 0,
        w: 0,
        h: 0,
      }
      const statics = [...state.items.values()].filter((i) => !start.has(i.id))
      drag.current = {
        primary: node.id,
        start,
        grabbed: dragged.map((n) => n.id),
        bounds,
        statics,
        cancelled: false,
      }
    },
    [state],
  )

  /** Where everything moving should be for the primary node at `pos`: ⇧ locks an axis, otherwise snap. */
  const layoutDrag = useCallback(
    (d: Drag, pos: Vec, shift: boolean) => {
      const s0 = d.start.get(d.primary)
      if (!s0) return null
      let delta = { x: pos.x - s0.x, y: pos.y - s0.y }
      let nextGuides: SnapGuides = { vertical: [], horizontal: [] }
      if (shift) delta = lockToAxis(delta)
      else {
        const snapped = computeSnap(d.bounds, delta, d.statics, flow.getZoom())
        delta = snapped.delta
        nextGuides = snapped.guides
      }
      const next = new Map<string, Vec>()
      for (const [id, p] of d.start) next.set(id, { x: p.x + delta.x, y: p.y + delta.y })
      return { next, guides: nextGuides }
    },
    [flow],
  )

  const onNodeDrag = useCallback(
    (e: { shiftKey: boolean }, node: Node) => {
      const d = drag.current
      if (!d || d.cancelled) return
      const r = layoutDrag(d, node.position, e.shiftKey)
      if (!r) return
      setDragPos(r.next)
      setGuides(r.guides)
    },
    [layoutDrag, setDragPos],
  )

  const { onMoveEnd } = props
  const onNodeDragStop = useCallback(
    (e: { altKey: boolean; shiftKey: boolean }, node: Node) => {
      const d = drag.current
      drag.current = null
      setGuides({ vertical: [], horizontal: [] })
      setDragPos(new Map())
      if (!d || d.cancelled) return
      // Recompute from the final pointer position: the last move may not have
      // produced a drag event before the release.
      const pos = layoutDrag(d, node.position, e.shiftKey)?.next ?? dragPosRef.current
      const positions = new Map<string, Vec>()
      for (const [id, p] of pos) {
        const s0 = d.start.get(id)
        if (s0 && Math.round(s0.x) === Math.round(p.x) && Math.round(s0.y) === Math.round(p.y))
          continue
        positions.set(id, { x: Math.round(p.x), y: Math.round(p.y) })
      }
      if (positions.size) onMoveEnd({ positions, grabbed: d.grabbed, duplicate: e.altKey })
    },
    [onMoveEnd, layoutDrag, setDragPos],
  )

  // ---------- keyboard: Space to pan, Esc cancels a drag ----------

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (isComposing(e) || isEditable(e.target)) return
      if (e.key === ' ') {
        e.preventDefault()
        if (!e.repeat) setSpaceHeld(true)
      } else if (e.key === 'Escape' && drag.current) {
        drag.current.cancelled = true
        setDragPos(new Map())
        setGuides({ vertical: [], horizontal: [] })
      }
    }
    const up = (e: KeyboardEvent) => {
      if (e.key === ' ') setSpaceHeld(false)
    }
    const blur = () => {
      setSpaceHeld(false)
    }
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    window.addEventListener('blur', blur)
    return () => {
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', up)
      window.removeEventListener('blur', blur)
    }
  }, [setDragPos])

  // ---------- viewport persistence ----------

  const viewKey = `viewport:${props.viewportKey}`
  const savedViewport = useMemo((): Viewport | null => {
    try {
      const raw = readPref(viewKey)
      return raw ? (JSON.parse(raw) as Viewport) : null
    } catch {
      return null
    }
  }, [viewKey])
  const fitted = useRef(false)
  useEffect(() => {
    if (!props.ready || fitted.current) return
    fitted.current = true
    if (savedViewport || !state.items.size) {
      firstView.resolve()
      return
    }
    requestAnimationFrame(() => {
      void flow.fitView({ padding: 0.2, maxZoom: 1 }).then(() => {
        firstView.resolve()
      })
    })
  }, [props.ready, savedViewport, state.items.size, flow, firstView])

  const soleSelected = selectedItems.size === 1 ? ([...selectedItems][0] ?? null) : null
  const callbacks = useMemo(
    () => ({
      onResizeEnd: props.onResizeEnd,
      canResize: props.canResize,
      onResetHeight: props.onResetHeight,
      soleSelected,
    }),
    [props.onResizeEnd, props.canResize, props.onResetHeight, soleSelected],
  )

  const { onPaneDoubleClick, onConnect, onDelete, interactive, editable = true } = props
  return (
    <div
      ref={wrapper}
      className={clsx(
        'moot-canvas relative h-full',
        spaceHeld && 'is-panning',
        !editable && 'is-readonly',
      )}
      onPointerDown={(e) => {
        // Clicking empty canvas ends editing. WebKit keeps focus in the editor
        // otherwise, and the next keystrokes would land in the card.
        if (!(e.target as Element).classList.contains('react-flow__pane')) return
        const active = document.activeElement
        if (active instanceof HTMLElement && wrapper.current?.contains(active)) active.blur()
      }}
      onDoubleClick={(e) => {
        if (!(e.target as Element).classList.contains('react-flow__pane')) return
        onPaneDoubleClick(flow.screenToFlowPosition({ x: e.clientX, y: e.clientY }))
      }}
    >
      <NodeCallbacksContext value={callbacks}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeDragStart={onNodeDragStart}
          onNodeDrag={onNodeDrag}
          onNodeDragStop={onNodeDragStop}
          onNodeClick={(_, node) => {
            props.onItemClick(node.id)
          }}
          onDelete={({ nodes: ns, edges: es }) => {
            onDelete(
              ns.map((n) => n.id),
              es.map((x) => x.id),
            )
          }}
          onConnect={({ source, target }) => {
            onConnect(source, target)
          }}
          connectionMode={ConnectionMode.Loose}
          connectionRadius={36}
          defaultViewport={savedViewport ?? { x: 0, y: 0, zoom: 1 }}
          onMove={() => {
            for (const fn of viewportListeners.current) fn()
          }}
          onMoveEnd={(_, vp) => {
            writePref(viewKey, JSON.stringify(vp))
          }}
          minZoom={0.1}
          maxZoom={2.5}
          panOnScroll
          panOnScrollSpeed={1}
          zoomOnScroll={false}
          zoomOnPinch
          zoomOnDoubleClick={false}
          zoomActivationKeyCode={['Meta', 'Control']}
          selectionOnDrag={interactive && !spaceHeld}
          selectionMode={SelectionMode.Partial}
          panOnDrag={!interactive ? false : spaceHeld ? true : [1, 2]}
          nodesDraggable={interactive && editable}
          nodesConnectable={editable}
          panActivationKeyCode={null}
          selectionKeyCode={null}
          deleteKeyCode={['Backspace', 'Delete']}
          multiSelectionKeyCode={['Shift', 'Meta']}
          elevateNodesOnSelect={false}
          proOptions={{ hideAttribution: true }}
        >
          <BoardBackground />
          <Guides guides={guides} />
          {props.worldOverlay && <ViewportPortal>{props.worldOverlay}</ViewportPortal>}
          <ZoomControls />
        </ReactFlow>
      </NodeCallbacksContext>
    </div>
  )
}
